const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'video[controls]',
  'audio[controls]',
  '[contenteditable="true"]',
].join(', ');

class FocusTrap {
  constructor(container) {
    this.container = container;
    this.onKeydown = this.handleKeydown.bind(this);
    this.container.addEventListener('keydown', this.onKeydown);
  }

  getFocusableElements() {
    return Array.from(this.container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => !!el.offsetParent,
    );
  }

  handleKeydown(event) {
    if (event.key !== 'Tab') return;

    const focusables = this.getFocusableElements();
    if (focusables.length === 0) return;

    const firstFocusable = focusables[0];
    const lastFocusable = focusables[focusables.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  destroy() {
    this.container.removeEventListener('keydown', this.onKeydown);
  }
}

class GiftingDrawer extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('#gifting-form');
    this.submitButton = this.querySelector('[data-gifting-submit]');
    this.firstNameInput = this.querySelector('#recipientFirstName');
    this.lastNameInput = this.querySelector('#recipientLastName');
    this.emailInput = this.querySelector('#recipientEmail');
    this.messageInput = this.querySelector('#gift_message');
    this.focusTrap = null;
    this._triggerElement = null;

    // Create aria-live region for announcements
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('role', 'alert');
    this.liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    document.body.appendChild(this.liveRegion);
    
    this.submitButton?.addEventListener('click', () => this.handleSubmit());

    // Add validation on input change
    this.firstNameInput?.addEventListener('blur', () => this.validateName(this.firstNameInput, 'firstNameError'));
    this.lastNameInput?.addEventListener('blur', () => this.validateName(this.lastNameInput, 'lastNameError'));
    this.emailInput?.addEventListener('blur', () => this.validateEmail(this.emailInput, 'emailError'));

    // Auto-save partial input so users don't lose typed values if they dismiss
    // the drawer (e.g. close + reopen via the gift-gate flow).
    const persist = () => this.persistDraft();
    this.firstNameInput?.addEventListener('input', persist);
    this.lastNameInput?.addEventListener('input', persist);
    this.emailInput?.addEventListener('input', persist);
    this.messageInput?.addEventListener('input', persist);
  }

  persistDraft() {
    try {
      const draft = {
        first_name: this.firstNameInput?.value || '',
        last_name: this.lastNameInput?.value || '',
        email: this.emailInput?.value || '',
        gift_message: this.messageInput?.value || ''
      };
      sessionStorage.setItem('wg_gift_draft', JSON.stringify(draft));
    } catch (e) {}
  }

  restoreDraft() {
    try {
      const raw = sessionStorage.getItem('wg_gift_draft');
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (this.firstNameInput && !this.firstNameInput.value && draft.first_name) this.firstNameInput.value = draft.first_name;
      if (this.lastNameInput && !this.lastNameInput.value && draft.last_name) this.lastNameInput.value = draft.last_name;
      if (this.emailInput && !this.emailInput.value && draft.email) this.emailInput.value = draft.email;
      if (this.messageInput && !this.messageInput.value && draft.gift_message) this.messageInput.value = draft.gift_message;
    } catch (e) {}
  }

  connectedCallback() {
    this.checkUrlParameter();
    document.addEventListener('gifting-drawer:open', () => this.open());

    // Close on Escape key
    this._onEscape = (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) {
        this.close();
  }
    };
    document.addEventListener('keydown', this._onEscape);
  }

  disconnectedCallback() {
    if (this._onEscape) {
      document.removeEventListener('keydown', this._onEscape);
    }
    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
    this.liveRegion?.remove();
  }

  checkUrlParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    const isGifting = urlParams.get('gifting') === 'true';
    const isProductPage = window.location.pathname.includes('/products/');
    const isDeferred = window.WILDGRAIN_DEFER_GIFT_DRAWER === true;

    console.log('[GiftingDrawer] checkUrlParameter:', { isGifting, isProductPage, isDeferred, pathname: window.location.pathname });

    if (isGifting && isProductPage && !isDeferred) {
      console.log('[GiftingDrawer] Opening drawer in 200ms...');
      setTimeout(() => {
        console.log('[GiftingDrawer] Opening drawer now');
        this.open();
      }, 200);
    } else if (!isGifting) {
      this.removeGiftRecipientCookie();
    }
  }

  open() {
    // Store the element that triggered the open so we can return focus on close
    this._triggerElement = document.activeElement;
    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    
    // Add active attribute after a small delay for animation
    requestAnimationFrame(() => {
      this.setAttribute('active', '');
    });

    // Prevent body scroll without layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    
    // Activate focus trap
    this.focusTrap = new FocusTrap(this);

    // Restore any draft input the user typed previously
    this.restoreDraft();

    // Announce to screen readers
    this.liveRegion.textContent = 'Gifting drawer is now open';

    // Focus first input
    setTimeout(() => {
      this.firstNameInput?.focus();
    }, 300);
  }

  close() {
    this.removeAttribute('active');
    this.removeAttribute('open');
    this.setAttribute('hidden', '');
    
    // Restore body scroll
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';

    // Destroy focus trap
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = null;
  }

    // Announce to screen readers
    this.liveRegion.textContent = 'Gifting drawer is now closed';

    // Return focus to the element that opened the drawer
    if (this._triggerElement) {
      this._triggerElement.focus();
      this._triggerElement = null;
    }
  }

  validateName(input, errorId) {
    const errorElement = document.getElementById(errorId);
    const value = input?.value.trim() || '';
    
    if (!value || value.length < 2) {
      input?.classList.remove('is-valid');
      input?.classList.add('is-invalid');
      if (errorElement) {
        errorElement.textContent = 'Please enter a valid name';
        errorElement.hidden = false;
      }
      return false;
    }
    
    input?.classList.remove('is-invalid');
    input?.classList.add('is-valid');
    if (errorElement) {
      errorElement.hidden = true;
    }
    return true;
  }

  validateEmail(input, errorId) {
    const errorElement = document.getElementById(errorId);
    const value = input?.value.trim() || '';
    const emailPattern = /^([\w-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
    
    if (!value || !emailPattern.test(value)) {
      input?.classList.remove('is-valid');
      input?.classList.add('is-invalid');
      if (errorElement) {
        errorElement.textContent = 'Please enter a valid email address';
        errorElement.hidden = false;
      }
      return false;
    }
    
    input?.classList.remove('is-invalid');
    input?.classList.add('is-valid');
    if (errorElement) {
      errorElement.hidden = true;
    }
    return true;
  }

  validateForm() {
    const firstNameValid = this.validateName(this.firstNameInput, 'firstNameError');
    const lastNameValid = this.validateName(this.lastNameInput, 'lastNameError');
    const emailValid = this.validateEmail(this.emailInput, 'emailError');
    
    return firstNameValid && lastNameValid && emailValid;
  }

  async handleSubmit() {
    if (!this.validateForm()) {
      const firstError = this.querySelector('.is-invalid');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstError.focus();
      }
      return;
    }
    this.submitButton.disabled = true;
    this.submitButton.textContent = 'Processing...';

    try {
      const recipientProperties = {
        first_name: this.firstNameInput?.value.trim() || '',
        last_name: this.lastNameInput?.value.trim() || '',
        email: this.emailInput?.value.trim() || '',
        gift_message: this.messageInput?.value.trim() || ''
      };
      this.saveRecipientProperties(recipientProperties);
      try { sessionStorage.removeItem('wg_gift_draft'); } catch (e) {}
      this.close();
      document.dispatchEvent(new CustomEvent('gifting:recipient-saved', {
        detail: recipientProperties
      }));
      this.showSuccessFeedback();
    } catch (error) {
      console.error('Error saving recipient properties:', error);
      alert('There was an error saving the recipient information. Please try again.');
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.textContent = this.submitButton.getAttribute('data-original-text') || 'CONTINUE WITH GIFT';
    }
  }

  saveRecipientProperties(properties) {
    const cookieValue = `recipientProperties=${JSON.stringify(properties)}; path=/; max-age=86400`;
    document.cookie = cookieValue;
  }

  removeGiftRecipientCookie() {
    document.cookie = 'recipientProperties=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  showSuccessFeedback() {
    const notification = document.createElement('div');
    notification.className = 'gifting-notification';
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');
    const icon = document.createElement('div');
    icon.className = 'gifting-notification__icon';
    icon.innerHTML = `
      <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="currentColor"/>
      </svg>
    `;
    const message = document.createElement('span');
    message.textContent = 'Recipient details have been saved';
    
    notification.appendChild(icon);
    notification.appendChild(message);
    document.body.appendChild(notification);
    requestAnimationFrame(() => {
      notification.classList.add('show');
    });
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

customElements.define('gifting-drawer', GiftingDrawer);

// Close drawer when clicking overlay or close button
document.addEventListener('click', (e) => {
  if (e.target.matches('[aria-controls="GiftingDrawer"]') ||
      e.target.closest('[aria-controls="GiftingDrawer"]')) {
    const drawer = document.getElementById('GiftingDrawer');
    if (drawer) {
      drawer.close();
    }
  }
});

// Save original button text on page load
document.addEventListener('DOMContentLoaded', () => {
  const submitButton = document.querySelector('[data-gifting-submit]');
  if (submitButton && !submitButton.hasAttribute('data-original-text')) {
    submitButton.setAttribute('data-original-text', submitButton.textContent);
  }
});

